import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { OpportunityNote } from '../../../../../js/api/models/OpportunityNote';
import type { PaginatedOpportunityNoteList } from '../../../../../js/api/models/PaginatedOpportunityNoteList';
import type { PatchedOpportunityNote } from '../../../../../js/api/models/PatchedOpportunityNote';
import { PatchedOpportunityStatusUpdate } from '../../../../../js/api/models/PatchedOpportunityStatusUpdate';
import { PatchedOpportunityUpdate } from '../../../../../js/api/models/PatchedOpportunityUpdate';
import { AssignmentsService } from '../../../../../js/api/services/AssignmentsService';
import { WorkflowsService } from '../../../../../js/api/services/WorkflowsService';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Avatar } from '../../../components/avatar';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../../components/dialog';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Fieldset, Field } from '../../../components/fieldset';
import { InlineDateEdit } from '../../../components/inline-date-edit';
import { InlineTextEdit } from '../../../components/inline-text-edit';
import { PageHeader } from '../../../components/PageHeader';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { Textarea } from '../../../components/textarea';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useDebounce } from '../../../hooks/useDebounce';
import { formatDateTo24Hr, parseApiLocalDate, parseFormDate } from '../../../utils/date';
import OpportunityForm from './OpportunityForm';
import {
  OPPORTUNITIES_LIST_QUERY_KEY,
  OPPORTUNITY_NOTES_QUERY_KEY,
  OPPORTUNITY_QUERY_KEY,
} from './queryKeys';

type OpportunityDrawerProps = {
  opportunityId: string;
};

type StatusMetadata = {
  is_initial?: boolean;
  is_draft?: boolean;
  is_final?: boolean;
  code: string;
  verb: string;
  title: string;
  set_by_healthcare_entity?: boolean;
};

const BRANDGREEN1 = 'brandgreen1' as const;
const STOPLIGHT_DANGER_OUTLINE = 'stoplight-danger-outline' as const;

const getStatusButtonStyling = (
  status: StatusMetadata
): 'brandgreen1' | 'stoplight-danger-outline' => {
  if (status.is_initial) {
    return BRANDGREEN1;
  }
  if (status.is_draft) {
    return BRANDGREEN1;
  }
  if (status.is_final) {
    return STOPLIGHT_DANGER_OUTLINE;
  }
  return BRANDGREEN1;
};

interface ConfirmationDialogProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: string;
  confirmText?: string;
  cancelText?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  show,
  onClose,
  onConfirm,
  title,
  body,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}) => {
  return (
    <Dialog open={show} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        <div dangerouslySetInnerHTML={{ __html: body }} />
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          {cancelText}
        </Button>
        <Button color="stoplight-danger" onClick={onConfirm}>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WORKFLOWS_META_QUERY_KEY = 'assignments.opportunity';

type OpportunityDetailsProps = {
  opportunity: Awaited<ReturnType<typeof AssignmentsService.assignmentsOpportunitiesRetrieve>>;
  specialtyMap: Record<string, string>;
};

function OpportunityDetails({ opportunity, specialtyMap }: OpportunityDetailsProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:bg-brand-green-0/10 tw:rounded-lg tw:p-4 tw:text-zinc-700">
      <span className="tw:!mb-0 tw:text-lg tw:font-bold">{opportunity.code}</span>
      <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-2 tw:gap-y-2 tw:mt-1 tw:items-center">
        <i className="fa-solid fa-hospital tw:text-brand-green-0 tw:justify-self-center" />
        <span className="tw:justify-self-start">
          {opportunity.facility?.name || 'Unknown Facility'}
        </span>
        <i className="fa-solid fa-location-dot tw:text-brand-green-0 tw:justify-self-center" />
        <span className="tw:justify-self-start">
          {opportunity.facility?.city ? `${opportunity.facility.city}, ` : ''}
          {opportunity.facility?.state ? `${opportunity.facility.state}` : ''}
          {!opportunity.facility?.city && !opportunity.facility?.state ? '--' : ''}
        </span>
        <i className="fa-solid fa-stethoscope tw:text-brand-green-0 tw:justify-self-center" />
        <span className="tw:justify-self-start">
          {specialtyMap[opportunity.specialty] || opportunity.specialty}
        </span>
      </div>
    </div>
  );
}

type DrawerSectionHeaderProps = {
  text: string;
  icon?: React.ReactNode;
};

function DrawerSectionHeader({ text, icon }: DrawerSectionHeaderProps) {
  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
      {icon}
      <span className="tw:text-brand-green-0 tw:italic tw:text-sm">{text}</span>
      {/* <hr className="tw:flex-1 tw:!border-brand-green-0" /> */}
    </div>
  );
}

type DrawerFieldProps = {
  label: string;
  value: React.ReactNode;
  whitespacePreLine?: boolean;
};

function DrawerField({ label, value, whitespacePreLine }: DrawerFieldProps) {
  return (
    <>
      <div className="tw:text-zinc-400 tw:text-sm tw:ml-5.75">{label}:</div>
      <div className={`tw:text-zinc-700 ${whitespacePreLine ? 'tw:whitespace-pre-line' : ''}`}>
        {value || '--'}
      </div>
    </>
  );
}

const noteFormSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

type NoteItemProps = {
  note: OpportunityNote;
  opportunityId: string;
};

function NoteItem({ note, opportunityId }: NoteItemProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const editNoteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: note.comment || '',
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async (data: PatchedOpportunityNote) => {
      return await AssignmentsService.assignmentsOpportunitiesNotePartialUpdate({
        id: note.id,
        requestBody: data,
      });
    },
    onSuccess: () => {
      toast.success('Note updated successfully');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating note' });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async () => {
      return await AssignmentsService.assignmentsOpportunitiesNoteDestroy({
        id: note.id,
      });
    },
    onSuccess: () => {
      toast.success('Note deleted successfully');
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error deleting note' });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
    editNoteForm.reset({
      comment: note.comment || '',
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    editNoteForm.reset({
      comment: note.comment || '',
    });
  };

  const handleSave = editNoteForm.handleSubmit(async (data) => {
    await updateNoteMutation.mutateAsync({
      comment: data.comment,
    });
  });

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    deleteNoteMutation.mutate();
    setShowDeleteConfirmation(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };
  const initials = note.created_by_name
    ? note.created_by_name
        .split(' ')
        .map((n) => n[0])
        .filter(Boolean)
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <>
      <ConfirmationDialog
        show={showDeleteConfirmation}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Note"
        body="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="tw:flex tw:gap-3">
        <Avatar
          initials={initials}
          className="tw:size-8 tw:shrink-0 tw:text-white tw:bg-zinc-600 tw:dark:bg-zinc-500"
        />
        <div className="tw:flex-1 tw:min-w-0">
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
            <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1 tw:mr-3">
              <span className="tw:text-sm tw:font-medium tw:text-zinc-900 tw:dark:text-zinc-100">
                {note.created_by_name}
              </span>
              <span className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                {formatDateTo24Hr(note.note_at, 'EE, MM/dd/yyyy HH:mm')}
              </span>
            </div>
            {note.allow_edit && (
              <div className="tw:flex tw:gap-2 tw:items-center tw:mb-1">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="tw:text-zinc-500 tw:hover:text-zinc-400 tw:dark:text-zinc-400 tw:dark:hover:text-zinc-300 tw:transition-colors"
                  aria-label="Edit note"
                >
                  <i className="fas fa-edit tw:text-sm" />
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteNoteMutation.isPending}
                  className="tw:text-stoplight-danger tw:hover:text-stoplight-danger/50 tw:dark:text-zinc-400 tw:dark:hover:text-stoplight-danger tw:transition-colors tw:disabled:opacity-50"
                  aria-label="Delete note"
                >
                  <i className="far fa-trash-can tw:text-sm" />
                </button>
              </div>
            )}
          </div>

          {isEditing ? (
            <form onSubmit={handleSave} className="tw:space-y-3">
              <div className="tw:bg-zinc-100 tw:dark:bg-zinc-800/50 tw:rounded-lg tw:p-3 tw:mb-1">
                <div className="tw:space-y-3">
                  <Textarea
                    {...editNoteForm.register('comment')}
                    placeholder="Enter a comment..."
                    rows={4}
                    disabled={updateNoteMutation.isPending}
                  />
                  {editNoteForm.formState.errors.comment && (
                    <FieldValidationErrorMessage
                      message={editNoteForm.formState.errors.comment.message}
                    />
                  )}
                </div>
              </div>
              <div className="tw:flex tw:justify-end tw:gap-2">
                <Button
                  type="button"
                  outline
                  onClick={handleCancel}
                  disabled={updateNoteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" color="brandgreen0" disabled={updateNoteMutation.isPending}>
                  {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="tw:flex tw:flex-col tw:gap-2 tw:bg-zinc-100 tw:dark:bg-zinc-800/50 tw:rounded-lg tw:p-3 tw:mb-1">
                <p className="tw:text-sm tw:text-zinc-900 tw:dark:text-zinc-100 tw:whitespace-pre-line">
                  {note.comment}
                </p>
              </div>
              <div className="tw:flex tw:items-center tw:justify-between">
                {note.was_edited && note.updated_at && (
                  <div className="tw:text-xs tw:text-zinc-500 tw:dark:text-zinc-400 tw:text-right">
                    Updated {formatDateTo24Hr(note.updated_at, 'EE, MM/dd/yyyy HH:mm')}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function OpportunityDrawer({ opportunityId }: OpportunityDrawerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [autoMatch, setAutoMatch] = useState<boolean | null>(null);
  const [maxAcceptedProviders, setMaxAcceptedProviders] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
    statusCode: string;
    statusName: string;
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [headerProps, setHeaderProps] = useState<{
    title: string;
    buttonHtml: React.ReactNode;
  } | null>(null);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Specialty',
    'Degree',
    'WorkType',
    'ShiftRateTypes',
  ]);

  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    error: opportunityError,
  } = useQuery({
    queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId],
    queryFn: () => AssignmentsService.assignmentsOpportunitiesRetrieve({ id: opportunityId }),
    enabled: !!opportunityId,
  });

  const { data: workflowMetadata } = useQuery({
    queryKey: ['workflowsMeta', WORKFLOWS_META_QUERY_KEY],
    queryFn: () =>
      WorkflowsService.workflowsMetaRetrieve({
        scope: [WORKFLOWS_META_QUERY_KEY],
      }),
  });

  const { data: notesData, isLoading: isLoadingNotes } = useQuery({
    queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId],
    queryFn: () =>
      AssignmentsService.assignmentsOpportunitiesNotesList({
        id: opportunityId,
        ordering: '-created_at', // Most recent first
      }),
    enabled: !!opportunityId,
    select: (data: PaginatedOpportunityNoteList | OpportunityNote | OpportunityNote[]) => {
      if (!data) {
        return [];
      }
      // Handle array of notes
      if (Array.isArray(data)) {
        return data;
      }
      // Handle paginated response
      if ('results' in data) {
        return data.results || [];
      }
      // Handle single note
      return [data as OpportunityNote];
    },
  });

  const notes = useMemo(() => {
    if (!notesData) {
      return [];
    }
    if (Array.isArray(notesData)) {
      return notesData;
    }
    return [];
  }, [notesData]);

  const noteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: '',
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: async (data: NoteFormData) => {
      return await AssignmentsService.assignmentsOpportunitiesNotesCreate({
        id: opportunityId,
        requestBody: {
          opportunity: opportunityId,
          comment: data.comment,
        } as OpportunityNote,
      });
    },
    onSuccess: () => {
      toast.success('Note added successfully');
      noteForm.reset();
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_NOTES_QUERY_KEY, opportunityId] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error adding note' });
    },
  });

  useEffect(() => {
    if (opportunity) {
      setAutoMatch(opportunity.settings?.auto_match ?? true);
      setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
      setStartDate(parseApiLocalDate(opportunity.starts_on));
    }
  }, [opportunity]);

  // Debounce max accepted providers input
  const debouncedMaxAcceptedProviders = useDebounce(maxAcceptedProviders, 500);

  const updateAutoMatchMutation = useMutation({
    mutationFn: async (value: boolean) => {
      return await AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            auto_match: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating auto match' });
      // Revert on error
      if (opportunity) {
        setAutoMatch(opportunity.settings?.auto_match ?? true);
      }
    },
  });

  const updateMaxAcceptedProvidersMutation = useMutation({
    mutationFn: async (value: number | null) => {
      return await AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          settings: {
            ...opportunity?.settings,
            max_accepted_connections: value,
          },
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating maximum accepted providers' });
      // Revert on error
      if (opportunity) {
        setMaxAcceptedProviders(opportunity.settings?.max_accepted_connections?.toString() ?? '');
      }
    },
  });

  const updateStartDateMutation = useMutation({
    mutationFn: async (value: string | null) => {
      return await AssignmentsService.assignmentsOpportunitiesPartialUpdate({
        id: opportunityId,
        requestBody: {
          starts_on: value,
        } as PatchedOpportunityUpdate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating start date' });
      // Revert on error
      if (opportunity) {
        setStartDate(parseApiLocalDate(opportunity.starts_on));
      }
    },
  });

  const statusUpdateMutation = useMutation({
    mutationFn: async (data: { id: string; statusCode: string }) => {
      const statusUpdateData: PatchedOpportunityStatusUpdate = {
        code: data.statusCode,
      };
      return await AssignmentsService.assignmentsOpportunitiesStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: (updatedOpportunity) => {
      toast.success('Opportunity status successfully updated');
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
      if (updatedOpportunity.need?.id) {
        queryClient.invalidateQueries({ queryKey: ['need', updatedOpportunity.need?.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['staffingNeeds', 'list'] });
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update opportunity status' });
    },
  });

  const handleAutoMatchChange = (checked: boolean) => {
    setAutoMatch(checked);
    updateAutoMatchMutation.mutate(checked);
  };

  const handleMaxAcceptedProvidersChange = (value: string) => {
    setMaxAcceptedProviders(value);
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    const dateStr = parseFormDate(date);
    updateStartDateMutation.mutate(dateStr);
  };

  // Effect to trigger debounced PATCH for max_accepted_connections
  useEffect(() => {
    if (opportunity && debouncedMaxAcceptedProviders !== undefined) {
      const numericValue =
        debouncedMaxAcceptedProviders === '' ? null : Number(debouncedMaxAcceptedProviders);
      const currentValue = opportunity.settings?.max_accepted_connections ?? null;

      // Only update if the value has actually changed and is a valid number (or null)
      if (
        numericValue !== currentValue &&
        (debouncedMaxAcceptedProviders === '' || !isNaN(Number(debouncedMaxAcceptedProviders)))
      ) {
        updateMaxAcceptedProvidersMutation.mutate(numericValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMaxAcceptedProviders]);

  // Handle duplicate click
  const handleDuplicateClick = () => {
    navigate(`/app/organization/opportunities/new?duplicate=${opportunityId}`);
  };

  // Handle status update
  const handleStatusUpdate = (status: StatusMetadata) => {
    if (!opportunityId) {
      toast.error('Opportunity ID not found');
      return;
    }

    if (status.is_final) {
      setPendingStatusUpdate({
        statusCode: status.code,
        statusName: status.verb,
      });
      setShowStatusConfirmation(true);
    } else {
      statusUpdateMutation.mutate({
        id: opportunityId,
        statusCode: status.code,
      });
    }
  };

  const confirmStatusUpdate = () => {
    if (pendingStatusUpdate && opportunityId) {
      statusUpdateMutation.mutate({
        id: opportunityId,
        statusCode: pendingStatusUpdate.statusCode,
      });
    }
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
  };

  const cancelStatusUpdate = () => {
    setShowStatusConfirmation(false);
    setPendingStatusUpdate(null);
  };

  // Get view mode buttons HTML (status transition buttons)
  const getViewModeButtonsHtml = () => {
    if (isLoadingOpportunity) {
      return <></>;
    }
    const currentStatusCode = opportunity?.current_status?.code;
    if (!currentStatusCode || !workflowMetadata?.[WORKFLOWS_META_QUERY_KEY]) {
      return <></>;
    }
    const allowedTransitions =
      workflowMetadata?.[WORKFLOWS_META_QUERY_KEY]?.[currentStatusCode]?.allowed_transitions;
    const allowedStatuses = allowedTransitions?.map(
      (transitionCode) => workflowMetadata?.[WORKFLOWS_META_QUERY_KEY]?.[transitionCode]
    );

    return (
      <>
        {allowedStatuses?.map((status) =>
          !status.set_by_healthcare_entity ? (
            <></>
          ) : (
            <Button
              small
              key={status.code}
              color={getStatusButtonStyling(status)}
              onClick={() => handleStatusUpdate(status)}
              disabled={statusUpdateMutation.isPending}
            >
              {status.verb}
            </Button>
          )
        )}
      </>
    );
  };

  if (isLoadingOpportunity) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading opportunity details...</div>
      </div>
    );
  }

  if (opportunityError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading opportunity details. Please try again.
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  const isClosed = opportunity.current_status?.title === 'Closed';
  const specialtyMap = codeDefinitionMaps?.Specialty || {};
  const degreeMap = codeDefinitionMaps?.Degree || {};
  const shiftRateTypeMap = codeDefinitionMaps?.ShiftRateTypes || {};

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    // Refresh opportunity data after closing modal
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
  };

  const handleEditSuccess = () => {
    // Refresh opportunity data after successful edit
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
    queryClient.invalidateQueries({ queryKey: [OPPORTUNITIES_LIST_QUERY_KEY] });
    setIsEditModalOpen(false);
  };

  return (
    <>
      <ConfirmationDialog
        show={showStatusConfirmation}
        onClose={cancelStatusUpdate}
        onConfirm={confirmStatusUpdate}
        title="Confirm Status Update"
        body={`Are you sure you want to ${pendingStatusUpdate?.statusName.toLowerCase()} this opportunity?`}
      />
      <Dialog
        open={isEditModalOpen}
        onClose={handleEditModalClose}
        className="tw:!max-w-6xl tw:!max-h-[calc(100vh-4rem)] tw:!flex tw:!flex-col tw:!p-0"
      >
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
          {headerProps && (
            <div className="tw:flex-shrink-0">
              <PageHeader
                className="tw:rounded-t-2xl"
                title={headerProps.title}
                buttonHtml={headerProps.buttonHtml}
              />
            </div>
          )}
          <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0">
            <DialogBody className="tw:mt-0">
              <OpportunityForm
                opportunityId={opportunityId}
                forceEditMode={true}
                onSuccess={handleEditSuccess}
                renderHeader={false}
                onHeaderRender={setHeaderProps}
              />
            </DialogBody>
          </div>
        </div>
      </Dialog>
      <div className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section */}
        <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
          {/* Left: Opportunity Details */}
          <div>
            <OpportunityDetails opportunity={opportunity} specialtyMap={specialtyMap} />
          </div>

          {/* Right: Status and Controls */}
          <div className="tw:flex tw:flex-col">
            <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:items-center">
              {/* Status */}
              <div className="tw:text-sm tw:font-semibold">Status:</div>
              <div>
                <StatusPill
                  status={opportunity.current_status?.title}
                  category={opportunity.current_status?.category}
                  small
                />
              </div>

              {/* Auto-Match */}
              <div className="tw:text-sm tw:font-semibold">Auto-Match:</div>
              <div className="tw:flex tw:items-center tw:gap-3">
                <BrandSwitch
                  checked={autoMatch ?? true}
                  onChange={handleAutoMatchChange}
                  disabled={isClosed || updateAutoMatchMutation.isPending}
                  small
                />
                <span className="tw:text-sm tw:text-zinc-500">{autoMatch ? 'On' : 'Off'}</span>
              </div>

              {/* Max Providers */}
              <div className="tw:text-sm tw:font-semibold">Max Providers:</div>
              <InlineTextEdit
                value={maxAcceptedProviders}
                onChange={handleMaxAcceptedProvidersChange}
                placeholder="Leave blank for unlimited..."
                displayValue={(val) => val || 'Unlimited'}
                small
                editOn="hover"
                disabled={isClosed || updateMaxAcceptedProvidersMutation.isPending}
                type="text"
                inputMode="numeric"
              />

              {/* Start Date */}
              <div className="tw:text-sm tw:font-semibold">Start Date:</div>
              <InlineDateEdit
                value={startDate}
                onChange={handleStartDateChange}
                placeholder="Select start date"
                small
                editOn="hover"
                disabled={isClosed || updateStartDateMutation.isPending}
              />
            </div>
            {/* Action Buttons */}
            <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mt-2">
              {getViewModeButtonsHtml()}
              {isClosed && (
                <Button small color="brand-green-outline" onClick={handleDuplicateClick}>
                  Duplicate
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Details and Connections */}
        <Tabs defaultTab="details" className="tw:mt-4">
          <TabsList>
            <TabsButton tab="details">Details</TabsButton>
          </TabsList>
          <TabsContent tab="details">
            <div className="tw:flex tw:justify-between tw:items-start tw:gap-6">
              <div className="tw:flex-1 tw:space-y-4">
                {/* Basic Information Section */}
                <section>
                  <DrawerSectionHeader
                    text="Basic Information"
                    icon={
                      <i className="fas fa-info-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Estimated Duration"
                      value={opportunity.estimated_duration}
                    />
                    <DrawerField
                      label="Description"
                      value={opportunity.description}
                      whitespacePreLine
                    />
                    <DrawerField
                      label="Additional Facilities"
                      value={
                        opportunity.alternate_facilities &&
                        Array.isArray(opportunity.alternate_facilities) &&
                        opportunity.alternate_facilities.length > 0
                          ? opportunity.alternate_facilities
                              .map((facility) =>
                                typeof facility === 'string' ? facility : facility.name
                              )
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Professional Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Professional Requirements"
                    icon={
                      <i className="fas fa-stethoscope tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Degrees"
                      value={
                        opportunity.degrees && opportunity.degrees.length > 0
                          ? opportunity.degrees.map((deg) => degreeMap[deg] || deg).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Availability"
                      value={
                        opportunity.work_types && opportunity.work_types.length > 0
                          ? opportunity.work_types.map((workType) => workType).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Life Support Certifications"
                      value={
                        opportunity.life_support_certifications &&
                        opportunity.life_support_certifications.length > 0
                          ? opportunity.life_support_certifications.map((cert) => cert).join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Board Certifications"
                      value={
                        opportunity.board_certifications &&
                        opportunity.board_certifications.length > 0
                          ? opportunity.board_certifications
                              .map((cert) =>
                                cert.boardEligible ? `${cert.value} (Allow Eligible)` : cert.value
                              )
                              .join(', ')
                          : null
                      }
                    />
                  </div>
                </section>

                {/* Eligibility Requirements Section */}
                <section>
                  <DrawerSectionHeader
                    text="Eligibility Requirements"
                    icon={
                      <i className="fas fa-check-circle tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Allow IMLC Providers"
                      value={opportunity.allow_imlc ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Allow Malpractice History"
                      value={opportunity.allow_malpractice_history ? 'Yes' : 'No'}
                    />
                    <DrawerField
                      label="Years of Experience"
                      value={opportunity.years_of_experience}
                    />
                    <DrawerField label="Shifts Completed" value={opportunity.shifts_completed} />
                    <DrawerField label="Additional Info" value={opportunity.additional_info} />
                  </div>
                </section>

                {/* Opportunity Settings Section */}
                <section>
                  <DrawerSectionHeader
                    text="Opportunity Settings"
                    icon={
                      <i className="fas fa-cog tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                    }
                  />
                  <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                    <DrawerField
                      label="Shift Rate Types"
                      value={
                        opportunity.shift_rate_types && opportunity.shift_rate_types.length > 0
                          ? opportunity.shift_rate_types
                              .map((shiftRateType) => {
                                const shiftRateTypeLabel =
                                  shiftRateTypeMap[shiftRateType] || shiftRateType;
                                // Map shift rate type to settings key for max rate
                                const shiftTypeToSettingsKey: Record<string, string> = {
                                  day: 'max_rate_day',
                                  night: 'max_rate_night',
                                  call: 'max_rate_call',
                                  '24h': 'max_rate_24h',
                                };
                                const rateKey = shiftTypeToSettingsKey[shiftRateType];
                                const rate = opportunity.settings?.[
                                  rateKey as keyof typeof opportunity.settings
                                ] as number | null | undefined;
                                const rateDisplay = rate ? `: $${rate}` : '';
                                return `${shiftRateTypeLabel}${rateDisplay}`;
                              })
                              .join(', ')
                          : null
                      }
                    />
                    <DrawerField
                      label="Created"
                      value={
                        opportunity.created_at
                          ? formatDateTo24Hr(opportunity.created_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                    <DrawerField
                      label="Last Updated"
                      value={
                        opportunity.updated_at
                          ? formatDateTo24Hr(opportunity.updated_at, 'MM/dd/yyyy HH:mm')
                          : null
                      }
                    />
                  </div>
                </section>
              </div>
              {opportunity.current_status?.title !== 'Closed' && (
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-0/80 tw:transition-colors tw:cursor-pointer"
                  aria-label="Edit opportunity"
                >
                  <i className="fa-solid fa-pen-to-square tw:text-lg" />
                </button>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Comment form */}
        <form
          onSubmit={noteForm.handleSubmit((data) => createNoteMutation.mutate(data))}
          className="tw:mt-4"
        >
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Textarea
                  {...noteForm.register('comment')}
                  placeholder="Enter a comment..."
                  rows={4}
                  disabled={createNoteMutation.isPending}
                />
                {noteForm.formState.errors.comment && (
                  <FieldValidationErrorMessage
                    message={noteForm.formState.errors.comment.message}
                  />
                )}
              </Field>
            </div>
            <div className="tw:flex tw:justify-end tw:mt-4">
              <Button type="submit" color="brandgreen0" disabled={createNoteMutation.isPending}>
                {createNoteMutation.isPending ? 'Adding...' : 'Add Comment'}
              </Button>
            </div>
          </Fieldset>
        </form>

        {/* Comments and Activity */}
        <Tabs defaultTab="comments">
          <TabsList>
            <TabsButton tab="comments">Comments</TabsButton>
          </TabsList>
          <TabsContent tab="comments">
            <div className="tw:space-y-6">
              {/* Notes List */}
              {isLoadingNotes ? (
                <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading comments...</div>
              ) : notes.length === 0 ? (
                <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No comments yet...</div>
              ) : (
                <div className="tw:space-y-4">
                  {notes.map((note) => (
                    <NoteItem key={note.id} note={note} opportunityId={opportunityId} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
